import React from 'react';
import { cn } from '@/lib/utils';

// Botanical corner flourish SVG
export const FloralCorner = ({ className, position = 'top-left' }) => {
  const positionClasses = {
    'top-left': 'top-0 left-0',
    'top-right': 'top-0 right-0 rotate-90',
    'bottom-left': 'bottom-0 left-0 -rotate-90',
    'bottom-right': 'bottom-0 right-0 rotate-180'
  };

  return (
    <svg
      className={cn(
        'absolute w-24 h-24 md:w-32 md:h-32 opacity-60 pointer-events-none',
        positionClasses[position],
        className
      )}
      viewBox="0 0 120 120"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      {/* Stem */}
      <path
        d="M10 110 Q 30 80 50 60 Q 70 40 90 20"
        stroke="hsl(138 25% 45%)"
        strokeWidth="1.5"
        fill="none"
        opacity="0.7"
      />
      {/* Leaf 1 */}
      <path
        d="M30 80 Q 20 70 25 55 Q 35 65 30 80"
        fill="hsl(138 25% 45%)"
        opacity="0.5"
      />
      {/* Leaf 2 */}
      <path
        d="M50 60 Q 60 45 70 50 Q 60 60 50 60"
        fill="hsl(138 25% 45%)"
        opacity="0.4"
      />
      {/* Flower petals */}
      <ellipse cx="90" cy="20" rx="8" ry="12" fill="hsl(18 55% 52%)" opacity="0.6" transform="rotate(-45 90 20)" />
      <ellipse cx="90" cy="20" rx="8" ry="12" fill="hsl(18 55% 52%)" opacity="0.5" transform="rotate(0 90 20)" />
      <ellipse cx="90" cy="20" rx="8" ry="12" fill="hsl(18 55% 52%)" opacity="0.4" transform="rotate(45 90 20)" />
      <ellipse cx="90" cy="20" rx="8" ry="12" fill="hsl(350 60% 70%)" opacity="0.5" transform="rotate(90 90 20)" />
      {/* Center */}
      <circle cx="90" cy="20" r="4" fill="hsl(45 85% 55%)" opacity="0.8" />
      {/* Small berries/buds */}
      <circle cx="45" cy="70" r="3" fill="hsl(18 55% 52%)" opacity="0.4" />
      <circle cx="65" cy="45" r="2.5" fill="hsl(138 25% 45%)" opacity="0.5" />
    </svg>
  );
};

// Vine divider
export const VineDivider = ({ className }) => (
  <svg
    className={cn('w-full h-10 md:h-12', className)}
    viewBox="0 0 800 50"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    preserveAspectRatio="xMidYMid meet"
  >
    {/* Main vine */}
    <path
      d="M0 25 Q 100 15 200 25 T 400 25 T 600 25 T 800 25"
      stroke="hsl(138 25% 45%)"
      strokeWidth="1.5"
      fill="none"
      opacity="0.5"
    />
    {/* Small leaves */}
    <path d="M150 23 Q 145 15 155 18" stroke="hsl(138 25% 45%)" strokeWidth="1" fill="none" opacity="0.4" />
    <path d="M350 27 Q 355 35 345 32" stroke="hsl(138 25% 45%)" strokeWidth="1" fill="none" opacity="0.4" />
    <path d="M550 23 Q 545 15 555 18" stroke="hsl(138 25% 45%)" strokeWidth="1" fill="none" opacity="0.4" />
    <path d="M650 27 Q 655 35 645 32" stroke="hsl(138 25% 45%)" strokeWidth="1" fill="none" opacity="0.4" />
    {/* Berries/flowers */}
    <circle cx="200" cy="25" r="4" fill="hsl(18 55% 52%)" opacity="0.5" />
    <circle cx="400" cy="25" r="5" fill="hsl(350 60% 70%)" opacity="0.4" />
    <circle cx="600" cy="25" r="4" fill="hsl(18 55% 52%)" opacity="0.5" />
  </svg>
);

// Section separator with flower
export const FloralSeparator = ({ className }) => (
  <div className={cn('relative py-8', className)}>
    <svg
      className="w-full h-16"
      viewBox="0 0 800 60"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      preserveAspectRatio="xMidYMid meet"
    >
      {/* Lines */}
      <line x1="0" y1="30" x2="340" y2="30" stroke="hsl(138 25% 45%)" strokeWidth="1" opacity="0.3" />
      <line x1="460" y1="30" x2="800" y2="30" stroke="hsl(138 25% 45%)" strokeWidth="1" opacity="0.3" />
      {/* Center flower */}
      <g transform="translate(400 30)">
        <ellipse cx="0" cy="0" rx="10" ry="16" fill="hsl(18 55% 52%)" opacity="0.6" transform="rotate(-36)" />
        <ellipse cx="0" cy="0" rx="10" ry="16" fill="hsl(18 55% 52%)" opacity="0.55" transform="rotate(0)" />
        <ellipse cx="0" cy="0" rx="10" ry="16" fill="hsl(18 55% 52%)" opacity="0.5" transform="rotate(36)" />
        <ellipse cx="0" cy="0" rx="10" ry="16" fill="hsl(350 60% 70%)" opacity="0.5" transform="rotate(72)" />
        <ellipse cx="0" cy="0" rx="10" ry="16" fill="hsl(350 60% 70%)" opacity="0.45" transform="rotate(108)" />
        <circle cx="0" cy="0" r="5" fill="hsl(45 85% 55%)" opacity="0.9" />
      </g>
      {/* Leaves */}
      <path d="M355 30 Q 345 20 355 15 Q 360 25 355 30" fill="hsl(138 25% 45%)" opacity="0.5" />
      <path d="M445 30 Q 455 20 445 15 Q 440 25 445 30" fill="hsl(138 25% 45%)" opacity="0.5" transform="scale(-1 1) translate(-890 0)" />
    </svg>
  </div>
);

// Pressed flower border wrapper
export const PressedFlowerFrame = ({ children, className }) => (
  <div className={cn('relative p-8', className)}>
    <FloralCorner position="top-left" />
    <FloralCorner position="top-right" />
    <FloralCorner position="bottom-left" />
    <FloralCorner position="bottom-right" />
    {children}
  </div>
);

// Decorative leaf
export const DecorativeLeaf = ({ className, variant = 'sage' }) => {
  const colors = {
    sage: 'hsl(138 25% 45%)',
    accent: 'hsl(18 55% 52%)',
    rose: 'hsl(350 60% 70%)'
  };

  return (
    <svg
      className={cn('w-8 h-8', className)}
      viewBox="0 0 32 32"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M16 2 Q 28 8 28 20 Q 20 28 16 30 Q 12 28 4 20 Q 4 8 16 2"
        fill={colors[variant]}
        opacity="0.7"
      />
      <path
        d="M16 8 L 16 26"
        stroke="hsl(45 30% 97%)"
        strokeWidth="1"
        opacity="0.5"
      />
      <path d="M16 12 L 10 16" stroke="hsl(45 30% 97%)" strokeWidth="0.5" opacity="0.4" />
      <path d="M16 16 L 22 20" stroke="hsl(45 30% 97%)" strokeWidth="0.5" opacity="0.4" />
      <path d="M16 20 L 10 24" stroke="hsl(45 30% 97%)" strokeWidth="0.5" opacity="0.4" />
    </svg>
  );
};

// Floating petals animation
export const FloatingPetals = ({ count = 5, className }) => {
  // Pre-computed petal positions for deterministic rendering
  const petalConfigs = [
    { left: 10, delay: 0, duration: 8, size: 10 },
    { left: 25, delay: 2, duration: 10, size: 12 },
    { left: 45, delay: 1, duration: 9, size: 8 },
    { left: 65, delay: 3, duration: 11, size: 14 },
    { left: 80, delay: 4, duration: 8, size: 10 },
    { left: 15, delay: 2.5, duration: 9.5, size: 11 },
    { left: 55, delay: 1.5, duration: 10.5, size: 9 },
    { left: 90, delay: 3.5, duration: 8.5, size: 13 },
  ];
  const petals = Array.from({ length: Math.min(count, petalConfigs.length) }, (_, i) => ({
    id: i,
    ...petalConfigs[i]
  }));

  return (
    <div className={cn('absolute inset-0 overflow-hidden pointer-events-none', className)}>
      {petals.map((petal) => (
        <svg
          key={petal.id}
          className="absolute opacity-30"
          style={{
            left: `${petal.left}%`,
            top: '-20px',
            width: `${petal.size}px`,
            height: `${petal.size * 1.5}px`,
            animation: `petal-fall ${petal.duration}s linear ${petal.delay}s infinite`
          }}
          viewBox="0 0 10 15"
          fill="none"
        >
          <ellipse
            cx="5"
            cy="7.5"
            rx="4"
            ry="7"
            fill={petal.id % 2 === 0 ? 'hsl(350 60% 70%)' : 'hsl(18 55% 52%)'}
          />
        </svg>
      ))}
    </div>
  );
};

// Paper layer background
export const PaperLayerBg = ({ children, className, layers = 2 }) => (
  <div className={cn('relative', className)}>
    {layers >= 2 && (
      <div
        className="absolute inset-0 bg-paper-dark rounded-xl -z-20"
        style={{ transform: 'translate(12px, 12px) rotate(-1.5deg)' }}
      />
    )}
    {layers >= 1 && (
      <div
        className="absolute inset-0 bg-paper-medium rounded-xl -z-10"
        style={{ transform: 'translate(6px, 6px) rotate(-0.75deg)' }}
      />
    )}
    <div className="relative bg-card rounded-xl shadow-raised">
      {children}
    </div>
  </div>
);

export default {
  FloralCorner,
  VineDivider,
  FloralSeparator,
  PressedFlowerFrame,
  DecorativeLeaf,
  FloatingPetals,
  PaperLayerBg
};
